import {
  isLocalhost,
  type SpotlightContext,
  spotlightContext,
  useSpotlightContext,
  useTeam,
  useTeamRole,
} from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import {
  showSuccessNotification,
  showNotification,
  hideNotification,
  showWarnNotification,
  useV2UI,
} from '@vrplatform/theme';
import {
  readSessionStorageValue,
  useClipboard,
  useLocalStorage,
} from '@mantine/hooks';
import {
  Spotlight,
  type SpotlightActionData as SpotlightAction,
} from '@mantine/spotlight';
import type React from 'react';
import { type ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { type NavigateFunction, useNavigate } from 'react-router';

import '@mantine/spotlight/styles.css';
import { useQueryDevTools } from '../../hooks';

type ReplaySession = {
  id: '4ceeda771c6946d2894697b70081d205';
  lastActivity: 1732241367458;
  previousSessionId: 'dc193f1554e84a26aa2d651168ccb200';
  sampled: 'session';
  segmentId: 23;
  started: 1732240509944;
};

type Ctx = {
  spotlight: SpotlightContext;
  navigate: NavigateFunction;

  toggleV2Ui: () => void;
  isV2Ui: boolean;

  toggleIsDarkTheme: () => void;
  isDarkTheme: boolean;

  copyToClipboard: (v: string) => void;
  teamId: string;

  setQueryDevTools: React.Dispatch<React.SetStateAction<boolean>>;

  isLocalApi: 'localhost:8877' | undefined;
  setIsLocalApi: (e: Ctx['isLocalApi']) => void;
};

type ActionFn = (ctx: Ctx) => SpotlightAction[];

const baseActions: ActionFn = ({ navigate }: Ctx) => [
  {
    group: 'Navigate',
    label: 'Home',
    id: 'home',
    description: 'Get to home page',
    onClick: () => navigate('/'),
    leftSection: <Icon icon="HomeIcon" size={18} />,
  },
];
const vrpAdminActions: ActionFn = ({ copyToClipboard, teamId }: Ctx) => {
  const actions: SpotlightAction[] = [];

  actions.push({
    label: 'Copy Team ID',
    group: 'VRP Admin Actions',
    description: `${teamId}`,
    id: 'copy-team-id',
    onClick: () => copyToClipboard(teamId),
    leftSection: <Icon icon="CopyIcon" size={18} />,
  });

  actions.push({
    group: 'VRP Admin Actions',
    label: 'Copy Session ID',
    id: 'copy-session-id',
    onClick: () => {
      const session = readSessionStorageValue<ReplaySession | undefined>({
        key: 'sentryReplaySession',
        defaultValue: undefined,
      });

      const id = session?.id;
      if (id) copyToClipboard(id);
      else
        showWarnNotification({
          title: 'Failed to copy',
          message: 'No session ID found',
        });
    },
    leftSection: <Icon icon="CopyIcon" size={18} />,
  });
  return actions;
};

const superAdminActions: ActionFn = ({
  spotlight,
  isDarkTheme,
  toggleIsDarkTheme,
  setQueryDevTools,
  isLocalApi,
  setIsLocalApi,
}: Ctx) => {
  const actions: SpotlightAction[] = [];

  actions.push({
    group: 'Super Admin Actions',
    label: isDarkTheme ? 'Disable Dark Theme' : 'Enable Dark Theme',
    description: 'Toggle Dark Theme',
    onClick: toggleIsDarkTheme,
    id: 'dark-theme',
    leftSection: <Icon icon="ToggleOnIcon" size={18} />,
  });

  if (window.location.hostname === 'localhost') {
    actions.push({
      id: 'query-dev-tools',
      group: 'Super Admin Actions',
      label: 'Query Dev Tools',
      description: 'Toggle Query Dev Tools',
      leftSection: <Icon icon="ToggleOnIcon" size={18} />,
      onClick: () => {
        setQueryDevTools((prev) => !prev);
      },
    });

    actions.push({
      id: 'api-host',
      group: 'Super Admin Actions',
      label: 'Toggle API Host',
      description: `Current: ${isLocalApi ? isLocalApi : 'HOSTED'}`,
      leftSection: <Icon icon="ToggleOnIcon" size={18} />,
      onClick: () => {
        setIsLocalApi(isLocalApi ? undefined : 'localhost:8877');
      },
    });
  }
  // actions.push({
  //   group: 'Super Admin Actions',
  //   label: isV2Ui ? 'Disable UI feature testing' : 'Enable UI feature testing',
  //   description: 'Toggle all UI features in development',
  //   onClick: toggleV2Ui,
  //   id: 'v2-ui',
  //   leftSection: <Icon icon="ToggleOnIcon" size={18} />,
  // });

  actions.push({
    label: 'Toggle Environment',
    group: 'Trigger',
    description: 'Toggle trigger environment',
    id: 'trigger-dev-key',
    onClick: () => {
      const env = spotlight.triggerEnv || 'production';

      spotlight.set({
        ...spotlight,
        triggerEnv:
          env === 'production'
            ? 'staging'
            : env === 'staging'
              ? 'dev'
              : undefined,
      });
    },
    leftSection: <Icon icon="Tag2Icon" size={18} />,
  });

  return actions;
};

export function SpotlightComponentProvider({
  children,
}: {
  children: ReactNode;
}) {
  const spotlight = useSpotlightContext();
  const navigate = useNavigate();
  const { copy, copied } = useClipboard();
  const {
    toggleV2: toggleV2Ui,
    isV2,
    isDarkTheme,
    toggleIsDarkTheme,
  } = useV2UI();
  const [{ id: teamId }] = useTeam();
  const { isVrpAdmin, isSuperAdmin } = useTeamRole();
  const copyToClipboard = useCallback((v: string) => copy(v), [copy]);
  const [isLocalApi, setLocalApi, resetLocalApi] = useLocalStorage<
    Ctx['isLocalApi']
  >({
    key: 'api-endpoint',
  });

  const { setQueryDevTools } = useQueryDevTools();

  const actions = useMemo(() => {
    const ctx: Ctx = {
      spotlight,
      navigate,
      teamId,
      toggleV2Ui,
      copyToClipboard,
      isV2Ui: isV2,
      isDarkTheme,
      toggleIsDarkTheme,
      setQueryDevTools,
      isLocalApi,
      setIsLocalApi: (e) => (e ? setLocalApi(e) : resetLocalApi()),
    };

    if (isSuperAdmin || isLocalhost) {
      return [
        ...baseActions(ctx),
        ...vrpAdminActions(ctx),
        ...superAdminActions(ctx),
      ];
    }
    if (isVrpAdmin) {
      return [...baseActions(ctx), ...vrpAdminActions(ctx)];
    }
    return baseActions(ctx);
  }, [
    spotlight,
    toggleV2Ui,
    teamId,
    isV2,
    isVrpAdmin,
    isDarkTheme,
    toggleIsDarkTheme,
    isSuperAdmin,
    copyToClipboard,
    navigate,
    setQueryDevTools,
    setLocalApi,
    isLocalApi,
    resetLocalApi,
  ]);

  useEffect(() => {
    if (copied)
      showSuccessNotification({
        icon: null,
        title: 'Success!',
        message: 'Successfully copied to clipboard.',
      });
  }, [copied]);

  return (
    <>
      {children}

      <Spotlight
        actions={actions}
        searchProps={{
          leftSection: <Icon icon="SearchIcon" size={18} />,
          placeholder: 'Search...',
        }}
        shortcut={['mod + P', 'mod + K', '/']}
        nothingFound="Nothing found..."
        radius="md"
        closeOnActionTrigger
        styles={{
          actionsGroup: {
            '::before': {
              textTransform: 'none',
              fontWeight: 500,
            },
          },
        }}
      />
    </>
  );
}

export function SpotlightConfigProvider({ children }: { children: ReactNode }) {
  const ref = useRef<SpotlightContext>({
    set(_state: SpotlightContext) {
      tes(_state.triggerEnv || '');
    },
  } as any);

  const [triggerEnv, tes] = useLocalStorage({ key: 'triggerEnv' });
  ref.current.triggerEnv = triggerEnv as any;

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const showDevNotification = (
      id: string,
      title: string,
      value: string | undefined
    ) => {
      hideNotification({ id });
      if (value)
        showNotification({
          id: id,
          onClick: (e) => {
            // did not click on close svg
            if ((e.target as any)?.nodeName !== 'svg') return;

            ref.current.set((state) => ({
              ...state,
              hypervisorQueue: undefined,
            }));
          },
          autoClose: false,
          title: title,
          message: value,
          color: 'orange',
        });
    };

    showDevNotification(
      'trigger-environment',
      'Trigger Environment',
      !ref.current.triggerEnv || ref.current.triggerEnv === 'production'
        ? undefined
        : ref.current.triggerEnv
    );
  }, [ref.current.triggerEnv]);

  return (
    <spotlightContext.Provider value={ref.current}>
      {children}
    </spotlightContext.Provider>
  );
}
